/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { Component, OnInit, OnDestroy, inject, signal } from "@angular/core";
import { CommonModule } from "@angular/common";
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { Subject, takeUntil } from "rxjs";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { TabsModule } from "primeng/tabs";
import { SelectModule } from "primeng/select";
import { InputTextModule } from "primeng/inputtext";
import { ButtonModule } from "primeng/button";
import { CardModule } from "primeng/card";
import { ProgressSpinnerModule } from "primeng/progressspinner";
import { MessageModule } from "primeng/message";
import { TooltipModule } from "primeng/tooltip";

import { CustomersService } from "../../services/customers.service";
import { SessionService } from "../../../../core/services/session.service";
import { QPilotAccessToken } from "../../../../core/services/qpilot.service";
// Use existing core services
import { StripeService } from "../../../../core/services/payment-gateways/stripe.service";
import { AuthorizeNetService } from "../../../../core/services/payment-gateways/authorize-net.service";
// Use existing core helpers
import { StripeHelper } from "../../../../core/services/payment-gateways/stripe.helper";
import { AuthorizeNetHelper } from "../../../../core/services/payment-gateways/authorize-net.helper";
// Feature helper for type display and conversion
import { PaymentIntegrationsHelper } from "../../services/payment-gateways/payment-integrations.helper";
import { PaymentMethod } from "../../interfaces/customer-payment-method.interface";
import { PaymentIntegration } from "../../interfaces/payment-integration.interface";
import {
  IAddPaymentMethodForm,
  ILoadFromGatewayForm,
} from "../../interfaces/add-payment-method-form.interface";
import { CountrySelectComponent } from "../../../../shared/country-select/country-select.component";
import { StateSelectComponent } from "../../../../shared/state-select/state-select.component";

@Component({
  selector: "app-add-payment-method-dialog",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TabsModule,
    SelectModule,
    InputTextModule,
    ButtonModule,
    CardModule,
    ProgressSpinnerModule,
    MessageModule,
    TooltipModule,
    CountrySelectComponent,
    StateSelectComponent,
  ],
  templateUrl: "./add-payment-method-dialog.component.html",
  styleUrl: "./add-payment-method-dialog.component.scss",
})
export class AddPaymentMethodDialogComponent implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private config = inject(DynamicDialogConfig);
  private dialogRef = inject(DynamicDialogRef);
  private customersService = inject(CustomersService);
  private sessionService = inject(SessionService);
  // Core services for API calls
  private stripeService = inject(StripeService);
  private authorizeNetService = inject(AuthorizeNetService);
  // Core helpers for mapping responses
  private stripeHelper = inject(StripeHelper);
  private authorizeNetHelper = inject(AuthorizeNetHelper);
  // Feature helper for display names and type conversion
  public helper = inject(PaymentIntegrationsHelper);

  // State signals
  paymentIntegrations = signal<PaymentIntegration[]>([]);
  externalMethods = signal<PaymentMethod[]>([]);
  loading = signal<boolean>(false);
  loadingExternal = signal<boolean>(false);
  error = signal<string | null>(null);
  selectedExternalMethod = signal<PaymentMethod | null>(null);

  // Tab state
  activeTab = 0;

  // Forms
  createForm!: FormGroup<IAddPaymentMethodForm>;
  loadForm!: FormGroup<ILoadFromGatewayForm>;

  // Dialog data
  customerId!: number;
  siteId!: number;
  existingMethods: PaymentMethod[] = [];
  embeddedMode: boolean = false;
  inputToken?: QPilotAccessToken;

  // Payment type options for dropdowns
  paymentTypeOptions: { label: string; value: string }[] = [];
  gatewayPaymentTypeOptions: { label: string; value: string }[] = [];

  private destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.customerId = this.config.data?.customerId;
    this.siteId = this.config.data?.siteId;
    this.existingMethods = this.config.data?.existingMethods || [];
    this.embeddedMode = this.config.data?.embeddedMode || false;
    this.inputToken = this.config.data?.inputToken;

    this.initializeForms();
    this.loadPaymentIntegrations();
  }

  /**
   * Get access token based on mode (embedded vs standard)
   */
  private getAccessToken(): QPilotAccessToken | undefined {
    if (this.embeddedMode && this.inputToken) {
      return this.inputToken;
    }
    return this.sessionService.accessToken ?? undefined;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeForms(): void {
    // Create new form
    this.createForm = this.fb.nonNullable.group({
      Type: ["", Validators.required],
      GatewayCustomerId: ["", Validators.required],
      GatewayPaymentId: ["", Validators.required],
      Description: ["", Validators.required],
      GatewayPaymentType: [null as number | null],
      BillingFirstName: [null as string | null],
      BillingLastName: [null as string | null],
      BillingCountry: [null as string | null],
      BillingState: [null as string | null],
      BillingStreet1: [null as string | null],
      BillingStreet2: [null as string | null],
      BillingCity: [null as string | null],
      BillingPostcode: [null as string | null],
    });

    // Load from gateway form
    this.loadForm = this.fb.nonNullable.group({
      Type: ["", Validators.required],
      GatewayCustomerId: ["", Validators.required],
    });

    // Subscribe to Type changes to update gateway payment type options
    this.createForm.controls.Type.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((type) => {
        this.updateGatewayPaymentTypeOptions(type);
      });

    // Subscribe to load form Type changes to auto-populate Gateway Customer ID
    this.loadForm.controls.Type.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((type) => {
        this.autoPopulateGatewayCustomerId(type);
      });
  }

  /**
   * Auto-populate Gateway Customer ID from existing payment methods
   */
  private autoPopulateGatewayCustomerId(type: string): void {
    if (!type) return;

    // Find an existing payment method with the same gateway type
    const existingMethod = this.existingMethods.find((m) => m.Type === type);
    console.log(
      "Auto-populating GatewayCustomerId for type:",
      type,
      existingMethod,
    );
    if (existingMethod?.GatewayCustomerId) {
      this.loadForm.controls.GatewayCustomerId.setValue(
        existingMethod.GatewayCustomerId,
      );
    } else {
      // Clear if no matching method found
      this.loadForm.controls.GatewayCustomerId.setValue("");
    }

    // Clear previous results when gateway changes
    this.externalMethods.set([]);
    this.selectedExternalMethod.set(null);
  }

  private loadPaymentIntegrations(): void {
    this.loading.set(true);
    const accessToken = this.getAccessToken();
    this.customersService
      .getPaymentIntegrationsForSite(this.siteId, accessToken)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (integrations) => {
          this.paymentIntegrations.set(integrations);
          this.buildPaymentTypeOptions(integrations);
          this.loading.set(false);
        },
        error: (err) => {
          console.error("Failed to load payment integrations:", err);
          this.error.set("Failed to load payment integrations");
          this.loading.set(false);
        },
      });
  }

  private buildPaymentTypeOptions(integrations: PaymentIntegration[]): void {
    this.paymentTypeOptions = integrations.map((integration) => ({
      label: this.helper.getTypeDisplayName(integration.PaymentMethodType),
      value: integration.PaymentMethodType,
    }));
  }

  private updateGatewayPaymentTypeOptions(type: string): void {
    const options = this.helper.getGatewayPaymentTypes(type);
    this.gatewayPaymentTypeOptions = options.map((opt) => ({
      label: opt.name,
      value: opt.code,
    }));
  }

  /**
   * Load payment methods from external gateway
   * Uses existing core services and helpers for API calls and mapping
   */
  loadFromGateway(): void {
    if (!this.loadForm.valid) return;

    const { Type, GatewayCustomerId } = this.loadForm.getRawValue();
    const integration = this.paymentIntegrations().find(
      (i) => i.PaymentMethodType === Type,
    );

    if (!integration) {
      this.error.set(`No integration found for ${Type}`);
      return;
    }

    this.loadingExternal.set(true);
    this.error.set(null);
    this.externalMethods.set([]);

    switch (Type) {
      case "Stripe":
        // Use existing core service for API call
        this.stripeService
          .getCustomerInformation(GatewayCustomerId, integration.ApiKey1)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (response) => {
              // Use existing core helper to map response
              const qpilotMethods = this.stripeHelper.mapProfiles(response);
              // Convert to feature's PaymentMethod interface
              const methods = this.helper.toPaymentMethods(qpilotMethods);
              this.externalMethods.set(methods);
              this.loadingExternal.set(false);
            },
            error: () => {
              this.error.set("Failed to load from Stripe");
              this.loadingExternal.set(false);
            },
          });
        break;

      case "AuthorizeNet":
        // Use existing core service for API call
        this.authorizeNetService
          .getCustomerInformation(
            GatewayCustomerId,
            integration.ApiAccount,
            integration.ApiKey1,
            integration.TestMode,
          )
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (response) => {
              // Use existing core helper to map response
              const qpilotMethods =
                this.authorizeNetHelper.mapProfiles(response);
              // Convert to feature's PaymentMethod interface
              const methods = this.helper.toPaymentMethods(qpilotMethods);
              this.externalMethods.set(methods);
              this.loadingExternal.set(false);
            },
            error: () => {
              this.error.set("Failed to load from Authorize.Net");
              this.loadingExternal.set(false);
            },
          });
        break;

      case "Braintree":
        // Braintree uses the QPilot API endpoint (different pattern)
        this.customersService
          .getOtherPaymentMethods(
            this.siteId,
            GatewayCustomerId,
            Type,
            this.getAccessToken(),
          )
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (methods) => {
              this.externalMethods.set(methods);
              this.loadingExternal.set(false);
            },
            error: () => {
              this.error.set("Failed to load from Braintree");
              this.loadingExternal.set(false);
            },
          });
        break;

      default:
        this.error.set(`Loading not supported for ${Type}`);
        this.loadingExternal.set(false);
    }
  }

  /**
   * Select an external method to add
   */
  selectExternalMethod(method: PaymentMethod): void {
    this.selectedExternalMethod.set(method);
  }

  /**
   * Check if method already exists
   */
  methodExists(method: PaymentMethod): boolean {
    return this.existingMethods.some(
      (m) =>
        m.GatewayPaymentId === method.GatewayPaymentId &&
        m.GatewayCustomerId === method.GatewayCustomerId,
    );
  }

  /**
   * Add new payment method (from create form)
   */
  addNewMethod(): void {
    if (!this.createForm.valid) {
      this.createForm.markAllAsTouched();
      return;
    }

    const formValue = this.createForm.getRawValue();
    const paymentMethod: Partial<PaymentMethod> = {
      CustomerId: this.customerId,
      Type: formValue.Type,
      GatewayCustomerId: formValue.GatewayCustomerId,
      GatewayPaymentId: formValue.GatewayPaymentId,
      Description: formValue.Description,
      GatewayPaymentType: formValue.GatewayPaymentType ?? undefined,
      BillingFirstName: formValue.BillingFirstName ?? undefined,
      BillingLastName: formValue.BillingLastName ?? undefined,
      BillingCountry: formValue.BillingCountry ?? undefined,
      BillingState: formValue.BillingState ?? undefined,
      BillingStreet1: formValue.BillingStreet1 ?? undefined,
      BillingStreet2: formValue.BillingStreet2 ?? undefined,
      BillingCity: formValue.BillingCity ?? undefined,
      BillingPostcode: formValue.BillingPostcode ?? undefined,
    };

    this.savePaymentMethod(paymentMethod);
  }

  /**
   * Add selected external method
   */
  addSelectedMethod(): void {
    const method = this.selectedExternalMethod();
    if (!method) return;

    const paymentMethod: Partial<PaymentMethod> = {
      ...method,
      CustomerId: this.customerId,
      Type: this.loadForm.getRawValue().Type,
    };

    this.savePaymentMethod(paymentMethod);
  }

  private savePaymentMethod(paymentMethod: Partial<PaymentMethod>): void {
    this.loading.set(true);
    const accessToken = this.getAccessToken();

    this.customersService
      .createPaymentMethod(paymentMethod, accessToken)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (created) => {
          this.dialogRef.close(created);
        },
        error: () => {
          this.error.set("Failed to create payment method");
          this.loading.set(false);
        },
      });
  }

  cancel(): void {
    this.dialogRef.close(null);
  }

  onTabChange(event: string | number): void {
    this.activeTab = Number(event);
  }

  /**
   * Check if a gateway supports external loading
   */
  supportsExternalLoad(type: string): boolean {
    return this.helper.supportsExternalLoad(type);
  }

  /**
   * Get available gateways that support external load
   */
  getLoadableGateways(): { label: string; value: string }[] {
    return this.paymentTypeOptions.filter((opt) =>
      this.helper.supportsExternalLoad(opt.value),
    );
  }
}
